import { common, model } from '@gosupersimple/types';
import { isArray } from 'lodash';

import { ChartAxis, ChartType } from '@/explore/types';
import { Json } from '@/lib/types';

import { CategoryValue } from '../category-chart/types';

export type SingleSeriesMetaData = {
  key: string;
  type: model.PropertyType | null;
  format?: common.PropertyValueFormat;
  label: string;
  compactLabel: string;
  axis: ChartAxis;
  chartType: ChartType;
  color: string;
  showValues: boolean;
  minValue: number;
  maxValue: number;
};

export type PrimaryGroupSeriesMetaData = SingleSeriesMetaData & {
  primaryGroupLabel: string;
};

export type SecondaryGroupSeriesMetaData = PrimaryGroupSeriesMetaData & {
  secondaryGroupLabel: string;
};

export type SeriesMetaData =
  | SingleSeriesMetaData
  | PrimaryGroupSeriesMetaData
  | SecondaryGroupSeriesMetaData;

export const isPrimaryGroupSeriesMetaData = (
  data: SeriesMetaData,
): data is PrimaryGroupSeriesMetaData =>
  'primaryGroupLabel' in data && !('secondaryGroupLabel' in data);

export const isSecondaryGroupSeriesMetaData = (
  data: SeriesMetaData,
): data is SecondaryGroupSeriesMetaData =>
  'primaryGroupLabel' in data && 'secondaryGroupLabel' in data;

export type TimeSeriesDataItem = {
  dateValue: Date;
  label: string;
  values: ItemValues;
};
export type TimeSeriesData = {
  minValue: number;
  maxValue: number;
  series: SeriesMetaData[];
  items: TimeSeriesDataItem[];
};

export type ItemValues = Record<string, number | null>;
export type GroupedItemValues = { group: string; values: ItemValues }[];
export const isGroupedValues = (values: ChartItemValues): values is GroupedItemValues =>
  'group' in values;

export type GroupedTimeSeriesDataItem = {
  dateValue: Date;
  label: string;
  values: GroupedItemValues;
};
export type GroupedTimeSeriesData = {
  minValue: number;
  maxValue: number;
  series: SeriesMetaData[];
  items: GroupedTimeSeriesDataItem[];
};

export const isGroupedTimeSeriesData = (data: ChartData): data is GroupedTimeSeriesData =>
  data.items.some(isGroupedTimeSeriesDataItem);

export const isGroupedTimeSeriesDataItem = (item: ChartItem): item is GroupedTimeSeriesDataItem =>
  'values' in item && isArray(item.values);

export type CategoryDataItem = {
  key: string;
  categoryValue: CategoryValue;
  label: string;
  values: ItemValues;
};

export type CategoryData = {
  maxValue: number;
  minValue: number;
  series: SeriesMetaData[];
  items: CategoryDataItem[];
};

export type ChartData = TimeSeriesData | CategoryData | GroupedTimeSeriesData;
export type ChartItem = ChartData['items'][0];
export type ChartItemValues = ChartItem['values'];

export type GroupedChartData<T = ChartData> =
  | {
      type: 'group';
      key: string;
      value: Json;
      label: string;
      items: GroupedChartData<T>;
    }[]
  | {
      type: 'data';
      chartData: T;
    };
